import { PERMISSION, PERMISSION_FLAG } from '../constants/permissions';
import ROUTER from '../routers/Routers';

// Giai đoạn chuyển tiếp cho payload cũ chưa có `access` (backend chưa cập nhật, hoặc cookie đăng nhập từ trước khi
// làm mới): suy quyền từ is_admin/permissions. Backend mới đã tự tính admin cũ thành is_super_admin + access đủ
// cờ, nên khi mọi payload đều có `access` thì đặt cờ này false rồi gỡ nhánh dự phòng. Đây là NƠI DUY NHẤT còn
// đọc is_admin/pm_role.
export const LEGACY_ADMIN_FALLBACK = true;

// Cờ mới -> action của permissions dạng thô (nhánh dự phòng): write cho cả create, edit và edit_status
const LEGACY_ACTIONS = {
  [PERMISSION_FLAG.VIEW]: ['read'],
  [PERMISSION_FLAG.CREATE]: ['write'],
  [PERMISSION_FLAG.EDIT]: ['write'],
  [PERMISSION_FLAG.EDIT_STATUS]: ['write', 'update_status'],
  [PERMISSION_FLAG.DELETE]: ['delete'],
};

const legacyCan = (user, code, flag) => {
  const actions = LEGACY_ACTIONS[flag] ?? [flag];
  const granted = user?.permissions?.some(
    p => p.code === code && actions.includes(p.action)
  );
  if (granted) return true;
  if (!LEGACY_ADMIN_FALLBACK || user?.is_admin !== true) return false;
  // admin cũ có mọi quyền, riêng pm_update đòi thêm pm_role (như `is_admin && pm_role` trước đây)
  return flag === PERMISSION_FLAG.PM_UPDATE ? user?.pm_role === true : true;
};

/**
 * Người dùng có cờ `flag` trên tài nguyên `code` không, theo user.access do backend tính sẵn. Backend vẫn kiểm
 * tra quyền ở mỗi request, hàm này chỉ để hiện/ẩn UI. Ví dụ: can(user, PERMISSION.PACKAGING_PRODUCTION_CARTON,
 * PERMISSION_FLAG.EDIT_STATUS).
 */
export const can = (user, code, flag) =>
  user?.access ? !!user.access[code]?.[flag] : legacyCan(user, code, flag);

// Duyệt/từ chối và cập nhật sản phẩm bán ra ngoài (trước đây `is_admin && pm_role`)
export const canPmUpdate = user =>
  can(user, PERMISSION.SALE_PRODUCTS, PERMISSION_FLAG.PM_UPDATE);

// Backend gộp create và edit thành một quyền write (orders:write, sale_products:write)
const canWrite = (user, code) =>
  isSuperAdmin(user) ||
  can(user, code, PERMISSION_FLAG.CREATE) ||
  can(user, code, PERMISSION_FLAG.EDIT);

// Thêm sản phẩm vào giỏ của khách khác (add_to_cart kèm customer_email đòi orders:write)
export const canWriteOrders = user => canWrite(user, PERMISSION.ORDERS);

// Tạo, sửa sản phẩm bán ra (publish_design, update_design, upload_images đòi sale_products:write)
export const canWriteSaleProducts = user =>
  canWrite(user, PERMISSION.SALE_PRODUCTS);

/**
 * Super admin (toàn quyền): role design:super_admin, hoặc admin cũ chưa gán role (backend mới trả is_super_admin = true).
 * Payload cũ chưa có is_super_admin thì suy từ is_admin (nhánh dự phòng).
 */
export const isSuperAdmin = user => {
  if (user?.is_super_admin !== undefined) return user.is_super_admin === true;
  return LEGACY_ADMIN_FALLBACK && user?.is_admin === true;
};

/**
 * Tài khoản nhân viên (có quyền với ít nhất một tài nguyên) khác khách hàng (access rỗng). Dùng cho các công cụ
 * dành cho nhân viên không gắn với một route backend cụ thể (vd công cụ thiết kế, thư viện module).
 */
export const isStaff = user => {
  if (user?.access) {
    return user.is_super_admin === true || Object.keys(user.access).length > 0;
  }
  return (
    (LEGACY_ADMIN_FALLBACK && user?.is_admin === true) ||
    (user?.permissions?.length ?? 0) > 0
  );
};

/**
 * Được lưu thiết kế và module vào thư viện (nút "Save design", "Save module"): super admin, hoặc tài khoản được cấp
 * riêng quyền tạo thiết kế (designs.create). Nhân viên khác thao tác như khách hàng.
 */
export const canSaveToLibrary = user =>
  isSuperAdmin(user) || can(user, PERMISSION.DESIGNS, PERMISSION_FLAG.CREATE);

/**
 * Được vào trang Khách hàng ở CMS (/cms/design-customers): có quyền xem hoặc chỉnh sửa khách hàng (customers). Việc làm
 * được gì trong trang (tạo, sửa, chốt giá) vẫn theo từng cờ.
 */
export const canAccessCustomers = user =>
  can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.VIEW) ||
  can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.EDIT);

/** Vào được CMS: super admin, hoặc tài khoản được vào trang Khách hàng (chỉ trang đó, xem canAccessCmsPage). */
export const canEnterCms = user =>
  isSuperAdmin(user) || canAccessCustomers(user);

/** Mở được trang `pathname` trong CMS không: super admin mọi trang; tài khoản khác chỉ trang Khách hàng. */
export const canAccessCmsPage = (user, pathname) => {
  if (isSuperAdmin(user)) return true;
  const customersPath = ROUTER.CMS + ROUTER.DESIGN_CUSTOMER;
  return (
    canAccessCustomers(user) &&
    (pathname === customersPath || pathname.startsWith(customersPath + '/'))
  );
};

// Chỉ giữ code và action (bỏ id) để user lưu trong cookie không vượt giới hạn ~4KB
export const compactPermissions = permissions =>
  Array.isArray(permissions)
    ? permissions.map(({ code, action }) => ({ code, action }))
    : [];
